import React, { useEffect, useMemo, useState } from "react";
import { TradingViewChartWidget } from "@crypto-trading/ui-shared/client";
import { PositionChart } from "./PositionChart";
import { CHART_INTERVAL_OPTIONS, Interval } from "@/hooks/usePositionPriceData";
import { useChartCandles, type CandleSource } from "@/hooks/useChartCandles";
import type { ExchangeCode } from "@/lib/market-data/market-data.types";
import {
  getPriceMarkets,
  peekPriceMarkets,
  toExchangeCode,
  toFuturesServiceExchange,
  toServiceBase,
  toServiceInterval,
  type FuturesServiceExchange,
  type ServiceMarket,
} from "@/lib/prices";
import { candleCacheKey, hasNoCandles } from "@/lib/prices/candle-cache";

/** Candles in view when the chart opens; older ones load while scrolling back. */
const VISIBLE_CANDLES = 80;

type MarketLookup =
  | { status: "loading" }
  | { status: "listed"; market: CandleSource }
  | { status: "unlisted" };

function findMarket(
  markets: ServiceMarket[],
  exchange: FuturesServiceExchange
): CandleSource | null {
  const listed = markets.find((market) => market.exchange === exchange);
  return listed ? { exchange: listed.exchange, symbol: listed.symbol } : null;
}

/** The selected exchange's futures market for the token, if the price service lists it. */
function useListedFuturesMarket(
  symbol: string,
  exchange: FuturesServiceExchange | null,
  enabled: boolean
): MarketLookup {
  const lookupKey = exchange && symbol ? `${exchange}:${toServiceBase(symbol)}` : null;
  const [resolved, setResolved] = useState<{
    key: string;
    market: CandleSource | null;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !lookupKey || !exchange) return;
    let cancelled = false;
    // Cached for 30 minutes by getPriceMarkets, so reopening costs no request.
    getPriceMarkets(symbol)
      .then((markets) => {
        if (!cancelled) {
          setResolved({ key: lookupKey, market: findMarket(markets, exchange) });
        }
      })
      .catch(() => {
        if (!cancelled) setResolved({ key: lookupKey, market: null });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, lookupKey, exchange, symbol]);

  return useMemo<MarketLookup>(() => {
    if (!lookupKey || !exchange) return { status: "unlisted" };
    let market: CandleSource | null;
    if (resolved?.key === lookupKey) {
      market = resolved.market;
    } else {
      const cached = peekPriceMarkets(symbol);
      if (!cached) return { status: "loading" };
      market = findMarket(cached, exchange);
    }
    return market ? { status: "listed", market } : { status: "unlisted" };
  }, [lookupKey, exchange, resolved, symbol]);
}

interface LiveMarketChartProps {
  userId: string;
  market: CandleSource;
  exchangeCode: ExchangeCode;
  enabled: boolean;
  /** TradingView symbol shown when this market has no candles. */
  backupSymbol: string;
}

const LiveMarketChart: React.FC<LiveMarketChartProps> = ({
  userId,
  market,
  exchangeCode,
  enabled,
  backupSymbol,
}) => {
  const hasCandles = (interval: Interval) =>
    !hasNoCandles(
      candleCacheKey(market.exchange, market.symbol, toServiceInterval(interval))
    );
  const [selectedInterval, setSelectedInterval] = useState<Interval>(
    () => CHART_INTERVAL_OPTIONS.find(hasCandles) ?? CHART_INTERVAL_OPTIONS[0]
  );

  const {
    priceChanges,
    isLoading,
    isLoadingMore,
    error,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
    historicalUnavailable,
  } = useChartCandles({
    userId,
    symbol: market.symbol,
    exchange: exchangeCode,
    interval: selectedInterval,
    startTime: null,
    endTime: null,
    enabled,
    liveMarket: market,
  });

  // Intervals whose first load came back empty stay hidden for the session.
  const availableIntervals = CHART_INTERVAL_OPTIONS.filter(hasCandles);
  const firstAvailable = availableIntervals[0] ?? null;
  const selectedUnavailable = !availableIntervals.includes(selectedInterval);

  useEffect(() => {
    if (selectedUnavailable && firstAvailable) {
      setSelectedInterval(firstAvailable);
    }
  }, [selectedUnavailable, firstAvailable, historicalUnavailable]);

  const failedWithoutCandles =
    !isLoading && Boolean(error) && priceChanges.length === 0;
  if (!firstAvailable || failedWithoutCandles) {
    return (
      <div data-testid="trade-chart" data-chart-source="tradingview">
        <TradingViewChartWidget symbol={backupSymbol} />
      </div>
    );
  }

  return (
    <div data-testid="trade-chart" data-chart-source="price-service">
      <PositionChart
        priceChanges={priceChanges}
        openedAt={null}
        onRequestOlderCandles={loadOlderCandles}
        onRequestNewerCandles={loadNewerCandles}
        hasMoreHistory={hasMoreHistory}
        hasMoreFuture={hasMoreFuture}
        onFollowLiveChange={setFollowLive}
        viewportResetKey={`${market.exchange}:${market.symbol}:${selectedInterval}`}
        enabledIntervals={availableIntervals}
        selectedInterval={selectedInterval}
        onIntervalChange={setSelectedInterval}
        isLoading={isLoading || selectedUnavailable}
        isLoadingMore={isLoadingMore}
        error={error}
        initialVisibleBars={VISIBLE_CANDLES}
      />
    </div>
  );
};

interface TokenLiveChartProps {
  userId: string;
  /** The order symbol, also used for the TradingView backup. */
  symbol: string;
  /** The exchange the user trades on; candles come from its futures market only. */
  exchangeCode: string | undefined;
  /** False while the drawer is closed: nothing is fetched or subscribed. */
  enabled: boolean;
}

/**
 * Live candle chart of the selected exchange's futures market from the price
 * service. Falls back to the TradingView widget when the service does not
 * list the token there, cannot be reached, or has no candles for it.
 */
export const TokenLiveChart: React.FC<TokenLiveChartProps> = ({
  userId,
  symbol,
  exchangeCode,
  enabled,
}) => {
  const exchange = toFuturesServiceExchange(exchangeCode);
  const appExchangeCode = toExchangeCode(exchangeCode);
  const lookup = useListedFuturesMarket(symbol, exchange, enabled);

  if (lookup.status === "loading") {
    return (
      <div data-testid="trade-chart" data-chart-source="loading">
        <PositionChart priceChanges={[]} openedAt={null} isLoading />
      </div>
    );
  }

  if (lookup.status === "unlisted" || !appExchangeCode) {
    return (
      <div data-testid="trade-chart" data-chart-source="tradingview">
        <TradingViewChartWidget symbol={symbol} />
      </div>
    );
  }

  return (
    <LiveMarketChart
      key={`${lookup.market.exchange}:${lookup.market.symbol}`}
      userId={userId}
      market={lookup.market}
      exchangeCode={appExchangeCode}
      enabled={enabled}
      backupSymbol={symbol}
    />
  );
};
